/**
 * "HOW TO WIRE" — the receiver's contract, in a drawer, on the page where the
 * endpoint is being wired.
 *
 * This is DOCUMENTATION AS UI and it exists because the contract lives in three
 * places a panel user cannot see: `InitWebhookRequest`/`InitWebhookResponse` in
 * packages/agent, the firing code in edge-api, and `EndWebhookPayload` in the
 * jobs worker. The person pointing ServiceNow at us reads none of those. The
 * drawer is the one rendering of the contract they will actually meet, so IT
 * MUST TRACK THE TYPES: when a field is added to either payload, this file is
 * part of the same change or the panel starts documenting a lie.
 *
 * A drawer rather than a page, deliberately: the reader is mid-task — endpoint
 * form open, receiver code half-written in another window — and needs to glance
 * and go back, not navigate away and lose the form.
 */
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet'
import { Button } from '@/components/ui/button'

/** One field of a payload: mono name, type hint, one-sentence meaning. */
function Field({ name, type, children }: { name: string; type: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-0.5 border-b border-border/60 py-1.5 last:border-b-0">
      <span>
        <span className="mono text-[12.8px] font-semibold">{name}</span>{' '}
        <span className="mono text-xs text-muted-foreground">{type}</span>
      </span>
      <span className="text-xs text-muted-foreground">{children}</span>
    </div>
  )
}

/** A BAND, not a line of text — the drawer is dense reference prose and these
 *  are its only landmarks, so they get the panel's own header treatment. */
function SectionTitle({ children }: { children: React.ReactNode }) {
  return (
    <h3 className="mt-6 rounded-md border border-border bg-muted px-3 py-2 text-base font-semibold tracking-tight">
      {children}
    </h3>
  )
}

/** A tiny request/response example. The app's snippet look, minus the copy
 *  button — four lines is retyped faster than fetched. */
function ExampleBlock({ children }: { children: string }) {
  return (
    <pre className="mono mt-2 overflow-x-auto rounded-md border bg-muted/50 p-3 text-xs leading-relaxed">
      {children}
    </pre>
  )
}

function SubTitle({ children }: { children: React.ReactNode }) {
  return (
    <h4 className="mt-3 mb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
      {children}
    </h4>
  )
}

export function WebhookGuide() {
  return (
    <Sheet>
      <SheetTrigger asChild>
        <Button variant="outline" size="sm" className="h-8">
          How to wire
        </Button>
      </SheetTrigger>
      {/* 40% of the viewport — this is reference material with mono field names,
          and the sheet's default 384px wraps them. `!` because the base class
          pins width with a higher-specificity data-[side] variant. Phones keep
          the default three-quarter sheet. */}
      <SheetContent side="right" className="sm:!w-[40vw] sm:!max-w-none">
        {/* `min-h-0 flex-1`: the sheet is a flex column, and without a minimum
            of 0 this item grows past the viewport instead of scrolling. */}
        <div className="min-h-0 flex-1 overflow-y-auto px-4 pb-8">
          <SheetHeader className="px-0">
            <SheetTitle>Wiring a webhook receiver</SheetTitle>
            <SheetDescription>
              Both webhooks are plain HTTPS POSTs with a JSON body, from us to a URL you
              define here. An endpoint is just an address — nothing fires until an agent
              points one of its two slots at it.
            </SheetDescription>
          </SheetHeader>

          <SectionTitle>Every delivery, both kinds</SectionTitle>
          <p className="mt-1 text-xs text-muted-foreground">
            Signed per <strong>Standard Webhooks</strong>: the request carries{' '}
            <span className="mono">webhook-id</span>, <span className="mono">webhook-timestamp</span>{' '}
            and <span className="mono">webhook-signature</span> (HMAC-SHA256 over{' '}
            <span className="mono">id.timestamp.body</span>, keyed by the endpoint&rsquo;s{' '}
            <span className="mono">whsec_…</span> secret — shown on the endpoint&rsquo;s page).
            Verify it with any Standard Webhooks library. Custom headers you store on the
            endpoint ride along on every delivery, so an <span className="mono">Authorization</span>{' '}
            header your gateway requires goes there.
          </p>

          {/* ------------------------------------------------------------ init */}
          <SectionTitle>Init webhook — before the model connects</SectionTitle>
          <p className="mt-1 text-xs text-muted-foreground">
            Fires once per session, after the agent is loaded and before the provider
            connection, so your answer can shape the call. The budget is tight —{' '}
            <strong>3 seconds</strong> on the web path, <strong>2 seconds</strong> on the
            phone path — and it <strong>fails open</strong>: a slow, down or malformed
            receiver costs the call its personalisation, never the call itself.
          </p>

          <SubTitle>Your receiver gets (POST body)</SubTitle>
          <div>
            <Field name="sessionId" type="string">
              Our id for this session — also the <span className="mono">webhook-id</span> the
              signature is bound to.
            </Field>
            <Field name="agentId" type="string">Which agent is about to take the call.</Field>
            <Field name="params" type="object of strings">
              The client data payload, unverified: what the embedding page asserted
              (<span className="mono">data-client-payload</span>) on the web path; what the
              carrier gave us on the phone path (<span className="mono">from</span>,{' '}
              <span className="mono">to</span>, <span className="mono">callSid</span>). You are
              the party that can judge these.
            </Field>
            <Field name="instructions" type="string">
              The system prompt as it stands. Substitute your knowledge into it and return
              the result — templating happens on your side, never on ours.
            </Field>
            <Field name="greeting" type="string | null">
              The greeting as it stands; null when the agent has none.
            </Field>
            <Field name="surface" type="object">
              Where the caller is: <span className="mono">channel</span> (
              <span className="mono">browser</span> or <span className="mono">phone</span>),{' '}
              <span className="mono">widget</span> (the embed variant that started the call —{' '}
              <span className="mono">flag</span>, <span className="mono">chat</span>, … —{' '}
              <span className="mono">panel</span> from the control panel, null on a phone) and{' '}
              <span className="mono">screen</span>, true only where a{' '}
              <span className="mono">show_info</span> card can be seen: the chat flag and the panel.
              On a surface without a screen the card tool is already withheld from the model and
              the prompt told so; use this to keep your own greeting from promising one. A
              platform fact, not something the page asserted.
            </Field>
          </div>

          <SubTitle>Your receiver may return (2xx, JSON — every key optional)</SubTitle>
          <div>
            <Field name="instructions" type="string, ≤ 40,000 chars">
              Replaces the prompt for this session. Omit to keep the agent&rsquo;s own.
            </Field>
            <Field name="greeting" type="string ≤ 2,000 chars, or null">
              Replaces the greeting; <span className="mono">null</span> clears it. Omitting
              the key keeps the agent&rsquo;s own — absent and null are different answers.
            </Field>
            <Field name="params" type="object">
              Replaces the session&rsquo;s client data payload <em>wholesale</em>. The keys
              are <strong>yours to invent</strong> — there is no fixed vocabulary: a key is
              whatever your own HTTP tools and end-of-call receiver expect to find, matched
              exactly and case-sensitively (<span className="mono">userID</span> and{' '}
              <span className="mono">userid</span> are different keys). What you return is
              what the session&rsquo;s HTTP tools receive on every call (unless a tool&rsquo;s
              &ldquo;send client payload&rdquo; setting is off), what the session row stores,
              and what the end-of-call webhook reports. Values may be strings,
              numbers or booleans (stringified); nesting is refused. Bounds: 20 entries,
              keys ≤ 64 chars, values ≤ 256 chars.
            </Field>
          </div>
          <p className="mt-2 text-xs text-muted-foreground">
            Because the return <em>replaces</em> the whole object, <strong>adding data means
            echoing what you received and appending your own keys</strong>; dropping a key
            deletes it, and <span className="mono">{'{}'}</span> deletes them all. A session
            that arrives with a claimed <span className="mono">userID</span> can leave your
            server verified and enriched:
          </p>
          <ExampleBlock>{`// your receiver got
{ "params": { "userID": "u-42" } }

// you answer — the claim checked, two facts added
{
  "params": {
    "userID": "u-42",
    "verified": "true",
    "accountId": "A-981"
  }
}`}</ExampleBlock>
          <p className="mt-2 text-xs text-muted-foreground">
            The return list is an <strong>allowlist, not a merge</strong>: model, voice,
            tools and anything else in the response are ignored. One invalid field rejects
            the whole response — there is no partial apply. An empty body or plain{' '}
            <span className="mono">200</span> means &ldquo;change nothing&rdquo;.
          </p>

          {/* ------------------------------------------------------------- end */}
          <SectionTitle>End-of-call webhook — after finalisation</SectionTitle>
          <p className="mt-1 text-xs text-muted-foreground">
            Fires once per session from a background queue, only after the recording is
            stitched and the cost priced — that is why it cannot fire at hangup. Your
            receiver has <strong>10 seconds</strong> to answer. Any <strong>2xx</strong>{' '}
            acknowledges; 5xx and timeouts are retried with backoff; a <strong>4xx</strong>{' '}
            is taken as &ldquo;read and refused&rdquo; and never retried. A duplicate is
            possible in one rare window — dedupe on <span className="mono">webhook-id</span>,
            which is the session id.
          </p>

          <SubTitle>Your receiver gets (POST body)</SubTitle>
          <div>
            <Field name="sessionId / agentId / agentName / label" type="strings">
              Identity: the session, the agent (name as it was at call time), and the label
              the session was created with.
            </Field>
            <Field name="clientParams" type="object | null">
              The params the session ran with — after any init-webhook rewrite.
            </Field>
            <Field name="transport" type='"webrtc" | "sip"'>Web call or phone call.</Field>
            <Field name="startedAt / endedAt" type="ISO 8601 | null">
              <span className="mono">endedAt</span> is null when we never observed the end.
            </Field>
            <Field name="durationMs" type="number | null">
              Null when never measured — not the same as zero.
            </Field>
            <Field name="state / evidence" type="strings">
              How the session ended and what we can claim about its recording
              (<span className="mono">COMPLETE</span>, <span className="mono">PARTIAL</span>,{' '}
              <span className="mono">UNVERIFIED</span>, <span className="mono">ABSENT</span>).
              Read these before trusting completeness — new values may appear, so do not
              exhaustively match.
            </Field>
            <Field name="turnCount / toolCallCount" type="numbers">Totals for the session.</Field>
            <Field name="tokens" type="object">
              The provider&rsquo;s six token counters:{' '}
              <span className="mono">audioInput</span>, <span className="mono">audioOutput</span>,{' '}
              <span className="mono">textInput</span>, <span className="mono">textOutput</span>,{' '}
              <span className="mono">cachedAudioInput</span>,{' '}
              <span className="mono">cachedTextInput</span> (the cached pair are subsets of
              their modality&rsquo;s total).
            </Field>
            <Field name="costUsd" type="decimal string | null">
              Money as a string, never a float. Null until priced.
            </Field>
            <Field name="transcript" type="array of turns">
              Each turn: <span className="mono">n</span>, <span className="mono">userText</span>,{' '}
              <span className="mono">assistantText</span>, and a{' '}
              <span className="mono">timings</span> object (vadWait, admission, inference,
              network, tool, totalToFirstAudio — milliseconds, diagnostic, not SLA).
            </Field>
            <Field name="transcriptTruncated" type="true, only when present">
              The 2&nbsp;MB inline cap dropped the <em>oldest</em> turns. The full transcript
              stays fetchable from the API.
            </Field>
            <Field name="toolCalls" type="array">
              Every tool call in start order: <span className="mono">callId</span>,{' '}
              <span className="mono">turnN</span>, <span className="mono">name</span>, raw{' '}
              <span className="mono">args</span> (model output) and{' '}
              <span className="mono">result</span> (tool output), <span className="mono">ok</span>,{' '}
              <span className="mono">errorMessage</span>, timing, and{' '}
              <span className="mono">orphaned</span>. Own 2&nbsp;MB cap with its own{' '}
              <span className="mono">toolCallsTruncated</span> flag.
            </Field>
            <Field name="audio" type="array of {track, url}">
              Recording references, never presigned links: call each{' '}
              <span className="mono">url</span> with your API key when you actually want the
              bytes.
            </Field>
          </div>

          <SubTitle>Your receiver may return</SubTitle>
          <p className="text-xs text-muted-foreground">
            Nothing — the body is ignored. Answer any 2xx promptly; do slow work after
            acknowledging.
          </p>
        </div>
      </SheetContent>
    </Sheet>
  )
}
